import React, { useEffect, useRef } from "react";
import mock02 from '../assets/images/mock02.png';
import mock03 from '../assets/images/mock03.png';
import mock05 from '../assets/images/gramcast.gif';
import mock07 from '../assets/images/mock07.png';
import mock08 from '../assets/images/mock08.png';
import mock09 from '../assets/images/mock09.png';
import mock10 from '../assets/images/mock10.png';
import mock11 from '../assets/images/mock11.png';
import mock12 from '../assets/images/dilber.gif';
import mock13 from '../assets/images/midifren.gif';
import mock14 from '../assets/images/velocity.gif';
import mock15 from '../assets/images/peerweb.png';
import mock16 from '../assets/images/chainwise-logo.svg';
import mock17 from '../assets/images/codeprompt.png';
import mock18 from '../assets/images/popscore.png';
import imgPressboard from '../assets/images/prsb.png';
import imgMonoframe from '../assets/images/monof.png';
import img808 from '../assets/images/808.png';
import imgBrutus from '../assets/images/brutus.png';
import '../assets/styles/Project.scss';

/* ── OG images ── */
const ogClustr = "https://clustr.run/og.png";
const ogGitlike = "https://gitlike.dev/og.png";
const ogSnapsecret = "https://snapsecret.lol/meta.png";
const ogGifnail = "https://gifnail.com/i.imgur.com/pJyICf0.gif";
const ogUnshittyfy = "https://unshittyfy.com/og-image.png";
const ogTiksi = "https://tiksi.io/graph.png";
const ogFade = "https://opengraph.githubassets.com/1/Omodaka9375/fade";
const ogWake = "https://github.com/user-attachments/assets/4183a0bf-7ecd-4d2e-a468-bae32f2df467";

/* ── Project card with Mac window chrome ── */
type ProjectCardProps = {
    title: string;
    url: string;
    imgUrl?: string;
    imgSrc?: string;
    desc: string;
    featured?: boolean;
    delay?: number;
};

function ProjectCard({ title, url, imgUrl, imgSrc, desc, featured, delay = 0 }: ProjectCardProps) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(([e]) => {
            if (e.isIntersecting) { el.classList.add('revealed'); obs.unobserve(el); }
        }, { threshold: 0.15 });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    const imgLink = imgUrl || url;
    const img = imgSrc || imgUrl;

    return (
        <div
            ref={ref}
            className={`project scroll-reveal${featured ? ' featured' : ''}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            <div className="project-title-bar">
                <span className="mac-btn close" />
                <span className="mac-btn minimize" />
                <span className="mac-btn maximize" />
                <a href={url} target="_blank" rel="noreferrer" className="project-title-link">{title}</a>
            </div >
            <div className="project-body">
                <p>{desc}</p>
                <a href={imgLink} target="_blank" rel="noreferrer" className="project-img-wrap">
                    <img src={img} alt={title} />
                </a>
            </div >
        </div >
    );
}

function Project() {
    return(
    <div className="projects-container" id="projects">
        <h1 >Things I've Built</h1>

        {/* ── Security & AI ── */}
        <h3 className="category-label">Security & AI</h3>
        <div className="projects-grid">
            <ProjectCard featured title="FADE" url="https://github.com/Omodaka9375/fade" imgSrc={ogFade} delay={0}
                desc="Drop-in KV cache compression for LLM inference. Shrinks the cache 3.5–12× with near-baseline quality via tiered quantization and attention-aware eviction." />
            <ProjectCard featured title="WAKE" url="https://omodaka9375.github.io/wake/" imgSrc={ogWake} delay={80}
                desc="A post-mortem protocol for AI agents. When you're gone, your agent executes your succession plan — final messages, tiered access, and terminal directives." />
            <ProjectCard title="Raccine" url="https://github.com/Omodaka9375/Raccine" imgSrc={mock10} delay={160}
                desc="Award-winning open-source tool that catches ransomware before it encrypts your files." />
            <ProjectCard title="DeepPhish" url="https://github.com/Omodaka9375/DeepPhish" imgSrc={mock09} delay={240}
                desc="A browser extension that uses ML to tell you if a website is trying to steal your credentials." />
            <ProjectCard title="AI In A Shell" url="https://github.com/Omodaka9375/ai-in-a-shell" imgSrc="https://raw.githubusercontent.com/Omodaka9375/ai-in-a-shell/main/logo.svg" delay={320}
                desc="Single-file shell script that turns natural language into runnable commands. Supports OpenAI, Anthropic, Gemini. Context-aware (cwd, git), with auto-run, silent piping, and $EDITOR integration." />
            <ProjectCard title="StylePass" url="https://hackernoon.com/using-javascript-to-identify-unique-typing-styles-k61b35bi" imgSrc={mock08} delay={400}
                desc="Identifies you by how you type. Everyone has a unique rhythm — this tool learns yours." />
        </div >

        {/* ── Web Apps & Tools ── */}
        <h3 className="category-label">Web Apps & Tools</h3>
        <div className="projects-grid">
            <ProjectCard featured title="Clustr" url="https://clustr.run" imgSrc={ogClustr} delay={0}
                desc="Paste any text and watch it turn into an interactive knowledge graph. Great for spotting connections you'd miss reading linearly." />
            <ProjectCard featured title="SnapSecret" url="https://snapsecret.lol" imgSrc={ogSnapsecret} delay={80}
                desc="Send secrets that self-destruct. Set a timer, limit who can read it, add a password. Gone after reading." />
            <ProjectCard title="PressBoard" url="https://omodaka9375.github.io/pressboard/" imgSrc={imgPressboard} delay={160}
                desc="Design circuit boards you can 3D-print at home. Drag components, route traces, check your design, export to STL." />
            <ProjectCard title="MonoFrame" url="https://app.gitlike.dev/monoframe/" imgSrc={imgMonoframe} delay={240}
                desc="Sketch wireframes using Unicode box-drawing characters. Looks great in any monospace font, exports as text or PNG." />
            <ProjectCard title="GifNail" url="https://gifnail.com" imgSrc={ogGifnail} delay={320}
                desc="Make your shared links show animated GIFs instead of static previews. Just paste a URL and grab the meta tag." />
            <ProjectCard title="Unshittyfy" url="https://unshittyfy.com" imgSrc={ogUnshittyfy} delay={400}
                desc="A productivity app that doesn't sugarcoat it. Tracks your tasks, your excuses, and your shame. Has a leaderboard." />
            <ProjectCard title="CodePrompt" url="https://codeprompt.me/" imgUrl="https://github.com/Omodaka9375/code-prompt" imgSrc={mock17} delay={480}
                desc="Paste your codebase and get a clean, token-efficient prompt ready for any LLM." />
            <ProjectCard title="PopScore AI" url="https://popscore.app/" imgUrl="https://github.com/Omodaka9375/Intrinsic-Image-Popularity" imgSrc={mock18} delay={560}
                desc="Upload a photo and find out how likely it is to go viral. Powered by an image popularity model." />
            <ProjectCard title="Velocity.js" url="https://velocity-js.info" imgSrc={mock14} delay={640}
                desc="A JS library that watches what users do and prefetches the next resource before they click." />
        </div >

        {/* ── Audio & Creative ── */}
        <h3 className="category-label">Audio & Creative</h3>
        <div className="projects-grid">
            <ProjectCard featured title="808ify" url="https://omodaka9375.github.io/808ify" imgSrc={img808} delay={0}
                desc="A drum machine in your browser. Step sequencer, pattern chaining, EQ, and MIDI — no install needed." />
            <ProjectCard featured title="Brutus" url="https://omodaka9375.github.io/brutus" imgSrc={imgBrutus} delay={80}
                desc="A raw, brutalist synth for the browser. Hooks up to your MIDI controller, sequences patterns, mangles sound." />
            <ProjectCard title="Dilber" url="https://dilber.io" imgSrc={mock12} delay={160}
                desc="Write JavaScript, hear sound, see visuals — all at once, live in the browser." />
            <ProjectCard title="MIDIFren" url="https://github.com/Omodaka9375/MIDIfren" imgSrc={mock13} delay={240}
                desc="Drop in a song, pull out vocals, drums, bass as separate stems, then convert them to MIDI." />
            <ProjectCard title="SoundTerminal" url="https://github.com/Omodaka9375/soundterminal" imgSrc={mock02} delay={320}
                desc="Type ASCII patterns in a terminal and hear them play back as beats. Surprisingly fun." />
        </div >

        {/* ── Blockchain & P2P ── */}
        <h3 className="category-label">Blockchain & P2P</h3>
        <div className="projects-grid">
            <ProjectCard featured title="Tiksi" url="https://tiksi.io" imgSrc={ogTiksi} delay={0}
                desc="Buy and sell event tickets right inside Telegram. NFT-based, crypto payments, no scalping, no fakes." />
            <ProjectCard featured title="GitLike" url="https://gitlike.dev" imgSrc={ogGitlike} delay={80}
                desc="Like Git, but decentralized. Your repos live on IPFS, you sign in with Ethereum, and it federates." />
            <ProjectCard title="ChainWise" url="https://chainwise.me/" imgUrl="https://github.com/Omodaka9375/ChainWise" imgSrc={mock16} delay={160}
                desc="One JS library, 17+ blockchains. Generate wallets, sign transactions, manage keys — unified API." />
            <ProjectCard title="PeerWeb" url="https://peerweb.lol" imgSrc={mock15} delay={240}
                desc="Host a website that nobody can take down. Peer-to-peer, no servers, censorship-resistant." />
            <ProjectCard title="AuroraDB" url="https://www.npmjs.com/package/aurora-db" imgSrc={mock03} delay={320}
                desc="A smarter IndexedDB wrapper that makes browser storage feel like a real database. Powers several of my other projects." />
            <ProjectCard title="GramCast" url="https://gramcast.xyz/" imgSrc={mock05} delay={400}
                desc="A podcast app that runs inside Telegram. Decentralized storage via IPFS, built on AuroraDB." />
            <ProjectCard title="NFC ETH Wallet" url="https://hackernoon.com/heres-how-i-built-an-eth-wallet-on-a-cheap-nfc-sticker-er1j3wyd" imgSrc={mock11} delay={480}
                desc="Turned a cheap NFC sticker into an Ethereum cold wallet. 888 bytes was all it took." />
        </div >

    </div >
    );
}

export default Project;
